<template>
  <div
    data-test="grapics-marqueeSettings"
  >
    <data-filters
      v-if="dataProvider"
      :element="element"
    />
    <SidebarSettingsGroup
      title="Ticker Preview"
      collapsible
      :element-type="element.type"
      data-test="graphics-marqueeSettings-tickerPreview"
    >
      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Preview
          <input
            v-model="tickerPreviewing"
            type="checkbox"
            data-test="graphics-marqueeSettings-togglePreview"
            data-entity="graphics-marqueeSettings-togglePreview"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check"></span>
          </div>
        </label>
      </div>
    </SidebarSettingsGroup>
    <SidebarSettingsGroup
      title="Scroll Settings"
      collapsible
      :element-type="element.type"
      data-test="graphics-marqueeSettings-scrollSettings"
    >
      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Ticker Spacing</label>
        <label
          class="radio inline-radio"
          for="infinite"
        >Infinite
          <input
            id="infinite"
            v-model="tickerSpacing"
            type="radio"
            value="infinite"
            data-test="graphics-marqueeSettings-tickerSpacing-infinite"
            data-entity="graphics-marqueeSettings-tickerSpacing-infinite"
          >
          <div class="checkmark"></div>
        </label>
        <label
          class="radio inline-radio"
          for="spaced"
        >Spaced
          <input
            id="spaced"
            v-model="tickerSpacing"
            type="radio"
            value="spaced"
            data-test="graphics-marqueeSettings-tickerSpacing-spaced"
            data-entity="graphics-marqueeSettings-tickerSpacing-spaced"
          >
          <div class="checkmark"></div>
        </label>
      </div>
      <div class="sidebar-form-group scrollSpeed-group">
        <label
          for="scrollSpeed"
          class="checkbox header-checkbox"
        >Scroll Speed</label>
        <div class="flex flex-align-center scrollSpeed-group__controls">
          <div class="sidebar-input-sub-label">
            Slow
          </div>
          <div class="range-slider">
            <input
              v-model.number="speed"
              class="range-slider__input range-slider__input-reverse no-tooltip"
              type="range"
              name="scrollSpeed"
              min="500"
              max="4000"
              step="500"
              aria-label="Alternative slider input for Marquee Speed"
              data-test="graphics-marqueeSettings-scrollSpeed"
              data-entity="graphics-marqueeSettings-scrollSpeed"
            />
          </div>
          <div class="sidebar-input-sub-label">
            Fast
          </div>
        </div>
      </div>
      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Reverse Scroll Direction
          <input
            v-model="reverse"
            type="checkbox"
            data-test="graphics-marqueeSettings-toggleReverse"
            data-entity="graphics-marqueeSettings-toggleReverse"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check"></span>
          </div>
        </label>
      </div>
    </SidebarSettingsGroup>
    <SidebarSettingsGroup
      title="Style & Animation"
      collapsible
      :element-type="element.type"
      data-test="graphics-marqueeSettings-styleAndAnimation"
    >
      <template
        v-if="hideOpacityControls"
      >
        <opacity-settings v-model="opacity" />

        <gap-settings
          v-model="gap"
          action="updateTickerGap"
        />

        <vertical-align-settings
          v-model="verticalAlign"
          action="updateTickerVerticalAlign"
        />
      </template>

      <div class="sidebar-form-group">
        <border-radius
          v-model:top-left="borderTopLeftRadius"
          v-model:top-right="borderTopRightRadius"
          v-model:bottom-left="borderBottomLeftRadius"
          v-model:bottom-right="borderBottomRightRadius"
        />
      </div>

      <hr class="sidebar-divider" />

      <!-- todo - extract background color to component. -->
      <div class="sidebar-form-group">
        <label class="sidebar-form-label">Background color</label>
        <label
          class="radio inline-radio"
          for="solid"
        >Solid
          <input
            id="solid"
            v-model="fillStyle"
            type="radio"
            value="solid"
            data-test="graphics-marqueeSettings-fillStyle-solid"
          >
          <div class="checkmark"></div>
        </label>
        <label
          class="radio inline-radio"
          for="gradient"
        >Gradient
          <input
            id="gradient"
            v-model="fillStyle"
            type="radio"
            value="gradient"
            data-test="graphics-marqueeSettings-fillStyle-gradient"
          >
          <div class="checkmark"></div>
        </label>

        <div
          v-if="fillStyle == 'solid'"
          class="colorpicker text-center"
          data-test="graphics-marqueeSettings-solidBackgroundColor"
        >
          <colorpicker
            v-model="backgroundColor"
            :is-mapped="isMapped('backgroundColor')"
          />
          <span
            data-balloon="This setting is controlled by data. Unmap data to customize."
            data-balloon-pos="down"
            data-balloon-length="medium"
          >
            <span
              v-if="isMapped('backgroundColor')"
              class="fa fa-lock mapped-lock"
              data-test="graphics-marqueeSettings-lockBackgroundColor"
            />
          </span>
        </div>

        <div
          v-else
          class="settings-row settings-row-xs"
          data-test="graphics-marqueeSettings-gradientBackground"
        >
          <div class="settings-col-6 settings-col-xs">
            <div class="input-group">
              <validation-field as="input"
                v-model.number="gradientDirection"
                rules="required|numeric"
                type="number"
                name="gradientDirection"
                :class="{ 'is-invalid': validationErrors['gradientDirection'] }"
                data-test="graphics-marqueeSettings-setGradientDirection"
                data-entity="graphics-marqueeSettings-setGradientDirection"
              />
              <span class="input-group-addon symbol">°</span>
            </div>
          </div>
          <div class="settings-col-3 settings-col-xs colorpicker text-center">
            <colorpicker
              v-model="gradientColorOne"
              :is-mapped="isMapped('gradientColorOne')"
            />
            <span
              data-balloon="This setting is controlled by data. Unmap data to customize."
              data-balloon-pos="down"
              data-balloon-length="medium"
            >
              <span
                v-if="isMapped('gradientColorOne')"
                class="fa fa-lock mapped-lock"
                data-test="graphics-marqueeSettings-lockGradientColorOne"
              />
            </span>
          </div>
          <div class="settings-col-3 settings-col-xs colorpicker text-center">
            <colorpicker
              v-model="gradientColorTwo"
              :is-mapped="isMapped('gradientColorTwo')"
            />
            <span
              data-balloon="This setting is controlled by data. Unmap data to customize."
              data-balloon-pos="down-right"
              data-balloon-length="medium"
            >
              <span
                v-if="isMapped('gradientColorTwo')"
                class="fa fa-lock mapped-lock"
                data-test="graphics-marqueeSettings-lockGradientColorTwo"
              />
            </span>
          </div>
        </div>

        <!-- Map control -->
        <!-- TODO: remove once sheets can have more than one thing mapped -->
        <div
          v-if="dataProvider && dataProvider?.type !== 'sheet-data'"
          class="margin-top-sm"
        >
          <p
            v-if="isBackgroundColorMapped"
            class="sidebar-form-label margin-top-sm"
          >
            From Data Provider
          </p>

          <map-data-control
            v-if="fillStyle == 'solid'"
            :data-provider="dataProvider"
            :element="element"
            :scroll-offset="scrollOffset"
            property="backgroundColor"
            :mapped="isMapped('backgroundColor')"
            @update-map="mapToField"
            @clear-map="clearMapping"
          />
          <map-data-control
            v-if="fillStyle == 'gradient'"
            :data-provider="dataProvider"
            :scroll-offset="scrollOffset"
            :element="element"
            property="gradientColorOne"
            :mapped="isMapped('gradientColorOne')"
            @update-map="mapToField"
            @clear-map="clearMapping"
          />
          <map-data-control
            v-if="fillStyle == 'gradient'"
            :data-provider="dataProvider"
            :scroll-offset="scrollOffset"
            :element="element"
            property="gradientColorTwo"
            :mapped="isMapped('gradientColorTwo')"
            @update-map="mapToField"
            @clear-map="clearMapping"
          />
        </div>
      </div>

      <hr class="sidebar-divider" />
      <background-image-settings
        v-if="!isSocialIcon"
        :element="element"
        :scroll-offset="scrollOffset"
        @modal="(opts) => $emit('modal', opts)"
      />
      <map-data-control
        v-if="isSocialIcon"
        :data-provider="dataProvider"
        :element="element"
        label="Network Icon Style"
        control-type="select"
        property="backgroundImageUrl"
        :mapped="isMapped('backgroundImageUrl')"
        :no-unmap="true"
        @update-map="mapToField"
        @clear-map="clearMapping"
      />

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Border
          <input
            v-model="border"
            type="checkbox"
            data-test="graphics-marqueeSettings-toggleBorder"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check"></span>
          </div>
        </label>

        <template v-if="border">
          <div class="settings-row settings-row-xs">
            <div class="settings-col-6 settings-col-xs">
              <validation-field as="input"
                v-model.number="borderWidth"
                rules="required|numeric|min:0"
                type="number"
                name="borderWidth"
                min="0"
                :class="{ 'is-invalid': validationErrors['borderWidth'] }"
                data-test="graphics-marqueeSettings-setBorderWidth"
                data-entity="graphics-marqueeSettings-setBorderWidth"
              />
              <label class="sidebar-input-sub-label">Width</label>
            </div>
            <div class="settings-col-6 settings-col-xs colorpicker">
              <colorpicker
                v-model="borderColor"
                :is-mapped="isMapped('borderColor')"
              />
              <label class="sidebar-input-sub-label">
                Color
                <span
                  data-balloon="This setting is controlled by data. Unmap data to customize."
                  data-balloon-pos="down-right"
                  data-balloon-length="medium"
                >
                  <span
                    v-if="isMapped('borderColor')"
                    class="fa fa-lock mapped-lock"
                  />
                </span>
              </label>
            </div>
          </div>
          <!-- TODO: remove once sheets can have more than one thing mapped -->
          <div v-if="dataProvider && dataProvider?.type !== 'sheet-data'">
            <p
              v-if="isMapped('borderColor')"
              class="sidebar-form-label margin-bottom-sm"
              data-test="graphics-marqueeSettings-borderColorMapped"
            >
              From Data Provider
            </p>
            <map-data-control
              :data-provider="dataProvider"
              :scroll-offset="scrollOffset"
              :element="element"
              property="borderColor"
              :mapped="isMapped('borderColor')"
              @update-map="mapToField"
              @clear-map="clearMapping"
            />
          </div>
        </template>
      </div>

      <hr class="sidebar-divider" />

      <box-shadow-settings
        :element="element"
        :data-provider="dataProvider"
        :scroll-offset="scrollOffset"
      />

      <hr class="sidebar-divider" />

      <animation-settings :element="element"></animation-settings>
    </SidebarSettingsGroup>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SettingsMixins from '../../../mixins/settings-mixins';

import AnimationSettings from '../Animation.vue';
import OpacitySettings from '../Opacity.vue';
import GapSettings from '../Gap.vue';
import VerticalAlignSettings from '../VerticalAlignSettings.vue';
import BoxShadowSettings from '../BoxShadow.vue';
import BackgroundImageSettings from '../BackgroundImage.vue';
import MapDataControl from '../Data/MapDataControl.vue';
import BorderRadius from '../BorderRadius.vue';
import DataFilters from '../DataFilters.vue';

export default {
  emits: ['modal'],
  name: 'MarqueeSettingsComponent',
  components: {
    AnimationSettings,
    OpacitySettings,
    GapSettings,
    VerticalAlignSettings,
    BoxShadowSettings,
    BackgroundImageSettings,
    MapDataControl,
    BorderRadius,
    DataFilters,
  },
  mixins: [SettingsMixins],
  props: ['element', 'scrollOffset'],
  computed: {
    ...mapGetters([
      'isPreviewingTicker',
    ]),
    tickerPreviewing: {
      get() {
        return this.isPreviewingTicker(this.element.id);
      },
      set() {
        this.updateTickerPreviewing(this.element.id);
      },
    },
    tickerSpacing: {
      get() {
        return this.element.tickerSpacing;
      },
      set(v) {
        this.updateOneElementValue('tickerSpacing', v);
      },
    },
    speed: {
      get() {
        return this.element.speed || 2000;
      },
      set(v) {
        this.updateOneElementValue('speed', v);
      },
    },
    reverse: {
      get() {
        return this.element.reverse || false;
      },
      set(v) {
        this.updateOneElementValue('reverse', v);
      },
    },
    opacity: {
      get() {
        return this.element.styles.opacity;
      },
      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },
    verticalAlign: {
      get() {
        return this.element.verticalAlign;
      },
      set(v) {
        this.updateOneElementValue('verticalAlign', v);
      },
    },
    gap: {
      get() {
        return this.element.styles.gap;
      },
      set(v) {
        this.updateOneElementValue('styles.gap', v);
      },
    },
    hideOpacityControls() {
      return !this.isSocialIcon;
    },
    isSocialIcon() {
      return this.element.data?.type === 'social-network-attribution';
    },
    borderTopLeftRadius: {
      get() {
        return this.element.borderTopLeftRadius;
      },
      set(v) {
        this.updateOneElementValue('borderTopLeftRadius', v);
      },
    },
    borderTopRightRadius: {
      get() {
        return this.element.borderTopRightRadius;
      },
      set(v) {
        this.updateOneElementValue('borderTopRightRadius', v);
      },
    },
    borderBottomRightRadius: {
      get() {
        return this.element.borderBottomRightRadius;
      },
      set(v) {
        this.updateOneElementValue('borderBottomRightRadius', v);
      },
    },
    borderBottomLeftRadius: {
      get() {
        return this.element.borderBottomLeftRadius;
      },
      set(v) {
        this.updateOneElementValue('borderBottomLeftRadius', v);
      },
    },
    fillStyle: {
      get() {
        return this.element.fillStyle;
      },
      set(v) {
        this.updateOneElementValue('fillStyle', v);
      },
    },
    backgroundColor: {
      get() {
        return (this.isMapped('backgroundColor') && this.dataValue('backgroundColor')) || this.element.backgroundColor;
      },
      set(v) {
        this.updateOneElementValue('backgroundColor', v);
      },
    },
    gradientDirection: {
      get() {
        return this.element.gradientDirection;
      },
      set(v) {
        this.updateOneElementValue('gradientDirection', v);
      },
    },
    gradientColorOne: {
      get() {
        return (this.isMapped('gradientColorOne') && this.dataValue('gradientColorOne')) || this.element.gradientColorOne;
      },
      set(v) {
        this.updateOneElementValue('gradientColorOne', v);
      },
    },
    gradientColorTwo: {
      get() {
        return (this.isMapped('gradientColorTwo') && this.dataValue('gradientColorTwo')) || this.element.gradientColorTwo;
      },
      set(v) {
        this.updateOneElementValue('gradientColorTwo', v);
      },
    },
    border: {
      get() {
        return this.element.border;
      },
      set(v) {
        this.updateOneElementValue('border', v);
      },
    },
    borderWidth: {
      get() {
        return this.element.borderWidth;
      },
      set(v) {
        this.updateOneElementValue('borderWidth', v);
      },
    },
    borderColor: {
      get() {
        return (this.isMapped('borderColor') && this.dataValue('borderColor')) || this.element.borderColor;
      },
      set(v) {
        this.updateOneElementValue('borderColor', v);
      },
    },
  },
  beforeUnmount() {
    this.previewTickerOff(this.element.id);
  },
  methods: {
    ...mapActions([
      'updateTickerPreviewing',
      'previewTickerOff',
    ]),
  },
};
</script>
